import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  postDescargarDocumentos,
  getValidaciones,
  patchEstado,
} from "../../services/reporteKokam/validacionesritServiceEntregas";
import "../../styles/AprobadosKokam/ValidadorImagenes.css";

import config from "../../config";
const API_BASE = `${config.baseUrl}/api/kokam`;

const ITEMS_PER_PAGE = 50;

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────

export default function ValidadorImagenes() {
  const token = useSelector((state) => state.auth.user?.token);

  const [input,        setInput]       = useState("");
  const [loading,      setLoading]     = useState(false);
  const [loadingGet,   setLoadingGet]  = useState(false);
  const [resumen,      setResumen]     = useState(null);
  const [tabla,        setTabla]       = useState([]);
  const [error,        setError]       = useState(null);
  const [selectAll,    setSelectAll]   = useState(false);
  const [currentPage,  setCurrentPage] = useState(1);

  const cedulas = input
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  // ── Paginación ──
  const totalPages     = Math.ceil(tabla.length / ITEMS_PER_PAGE);
  const startIndex     = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTabla = tabla.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // ── Cargar tabla ──
  const cargarTabla = useCallback(async () => {
    setLoadingGet(true);
    try {
      const data = await getValidaciones(token);
      setTabla(data);
      setCurrentPage(1);
      const todosRechazados = data.length > 0 && data.every((r) => r.estado === 0);
      setSelectAll(todosRechazados);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingGet(false);
    }
  }, [token]);

  useEffect(() => {
    cargarTabla();
  }, [cargarTabla]);

  useEffect(() => {
    if (tabla.length === 0) { setSelectAll(false); return; }
    setSelectAll(tabla.every((r) => r.estado === 0));
  }, [tabla]);

  // ── Optimistic update individual ──
  const handleEstado = async (identificacion, estabaRechazado) => {
    const nuevoEstado = estabaRechazado ? 1 : 0;

    setTabla((prev) =>
      prev.map((r) =>
        r.identificacion === identificacion ? { ...r, estado: nuevoEstado } : r
      )
    );

    try {
      await patchEstado(identificacion, nuevoEstado, token);
    } catch {
      // Revertir si falla
      setTabla((prev) =>
        prev.map((r) =>
          r.identificacion === identificacion
            ? { ...r, estado: estabaRechazado ? 0 : 1 }
            : r
        )
      );
    }
  };

  // ── Seleccionar / deseleccionar todos ──
  const handleSelectAll = async (e) => {
    const checked     = e.target.checked;
    const nuevoEstado = checked ? 0 : 1;

    setSelectAll(checked);
    setTabla((prev) => prev.map((r) => ({ ...r, estado: nuevoEstado })));

    try {
      await Promise.allSettled(
        tabla.map((r) => patchEstado(r.identificacion, nuevoEstado, token))
      );
    } catch (e) {
      console.error("Error actualizando todos:", e);
    }
  };

  // ── Submit descarga ──
  const handleSubmit = async () => {
    if (!cedulas.length) return;
    setLoading(true);
    setError(null);
    setResumen(null);

    try {
      const resp = await postDescargarDocumentos(cedulas, token);
      setResumen(resp.data);
      setInput("");
      await cargarTabla();
    } catch (e) {
      setError(e?.message ?? "Error desconocido");
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setInput("");
    setResumen(null);
    setError(null);
  };

  // ── Paginación ──
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="vi-page">

      {/* ── SIDEBAR ── */}
      <div className="vi-sidebar">
        <div className="vi-sidebar-header">
          <span className="vi-tag">VALIDACIÓN RIT</span>
          <h2 className="vi-sidebar-title">Descarga de Imágenes</h2>
          <p className="vi-sidebar-sub">
            Ingresa las cédulas separadas por comas para descargar y registrar
            sus imágenes documentales.
          </p>
        </div>

        <div className="vi-sidebar-body">
          <label className="vi-label">
            Números de cédula
            {cedulas.length > 0 && (
              <span className="vi-counter">{cedulas.length} detectadas</span>
            )}
          </label>

          <textarea
            className="vi-textarea"
            placeholder="Ej: 1234567890, 0987654321"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            rows={6}
          />

          {cedulas.length > 0 && (
            <div className="vi-chips">
              {cedulas.slice(0, 8).map((c, i) => (
                <span key={i} className="vi-chip">{c}</span>
              ))}
              {cedulas.length > 8 && (
                <span className="vi-chip vi-chip--more">
                  +{cedulas.length - 8} más
                </span>
              )}
            </div>
          )}

          <div className="vi-actions">
            <button
              className="vi-btn-secondary"
              onClick={handleLimpiar}
              disabled={loading}
            >
              Limpiar
            </button>
            <button
              className="vi-btn-primary"
              onClick={handleSubmit}
              disabled={loading || !cedulas.length}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Spinner /> Procesando...
                </span>
              ) : (
                "Iniciar descarga"
              )}
            </button>
          </div>

          {error && (
            <div className="vi-alert-error">
              <span>✘</span>
              <span>{error}</span>
            </div>
          )}

          {resumen && (
            <div className="vi-resumen">
              <p className="vi-resumen-title">✔ Proceso finalizado</p>
              <div className="vi-stats-grid">
                <Stat label="Total"       value={resumen.total}            color="#334155" />
                <Stat label="Completos"   value={resumen.completos}        color="#16a34a" />
                <Stat label="Incompletos" value={resumen.parciales}        color="#b45309" />
                <Stat label="Sin imagen"  value={resumen.sinNingunaImagen} color="#b91c1c" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── TABLA ── */}
      <div className="vi-main">
        <div className="vi-table-header">
          <div>
            <h3 className="vi-table-title">Registros en validaciones_rit_entregas</h3>
            <p className="vi-table-sub">
              {loadingGet
                ? "Cargando..."
                : `${tabla.length} registros encontrados`}
            </p>
          </div>
          <button
            className="vi-btn-refresh"
            onClick={cargarTabla}
            disabled={loadingGet}
          >
            {loadingGet ? <Spinner dark /> : "↻ Actualizar"}
          </button>
        </div>

        <div className="vi-table-wrapper">
          {loadingGet && tabla.length === 0 ? (
            <div className="vi-empty-state">
              <Spinner dark />
              Cargando registros...
            </div>
          ) : tabla.length === 0 ? (
            <div className="vi-empty-state">
              <span className="vi-empty-icon">📂</span>
              <span>No hay validaciones registradas aún.</span>
            </div>
          ) : (
            <table className="vi-table">
              <thead>
                <tr>
                  <th className="vi-th-check">
                    <label className="vi-check-wrap" title="Marcar todos como rechazados">
                      <input
                        type="checkbox"
                        className="vi-checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <span className="vi-check-label">Rechazar</span>
                    </label>
                  </th>
                  <th>Cédula</th>
                  <th>Imágenes</th>
                  <th>Foto Beneficio</th>   {/* ✅ nombre real de la columna */}
                  <th>Doc. Frontal</th>
                  <th>Firma Personal</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTabla.map((row) => {
                  const rechazado = row.estado === 0;
                  return (
                    <tr
                      key={row.identificacion}
                      className={rechazado ? "vi-tr--rechazado" : ""}
                    >
                      <td className="vi-td-check">
                        <input
                          type="checkbox"
                          className="vi-checkbox"
                          checked={rechazado}
                          onChange={() => handleEstado(row.identificacion, rechazado)}
                          title={rechazado ? "Rechazado — click para aprobar" : "Aprobado — click para rechazar"}
                        />
                      </td>
                      <td className="vi-td-cedula">
                        {row.identificacion}
                      </td>
                      <td className="vi-td-center">
                        <ImagenBadge count={row.img_encontradas} />
                      </td>
                      <td className="vi-td-center">
                        {/* ✅ foto_beneficio — antes era row.doc_anverso (no existe en BD) */}
                        <ImagenCell ruta={row.foto_beneficio}  token={token} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.doc_frontal}    token={token} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.firma_personal} token={token} />
                      </td>
                      <td className="vi-td-fecha">
                        {rechazado && (
                          <span className="vi-badge-rechazado">Rechazado</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── PAGINADOR ── */}
        {totalPages > 1 && (
          <div className="vi-pagination">
            <span className="vi-pagination-info">
              Página {currentPage} de {totalPages}
              &nbsp;·&nbsp;
              {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, tabla.length)} de {tabla.length}
            </span>

            <div className="vi-pagination-controls">
              <button
                className="vi-page-btn"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                title="Primera página"
              >«</button>

              <button
                className="vi-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >‹</button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  className={`vi-page-btn ${currentPage === page ? "vi-page-btn--active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="vi-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Página siguiente"
              >›</button>

              <button
                className="vi-page-btn"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Última página"
              >»</button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────────

function ImagenCell({ ruta, token }) {
  const [open,     setOpen]     = useState(false);
  const [imgSrc,   setImgSrc]   = useState(null);
  const [imgError, setImgError] = useState(false);

  const nombre = ruta?.split(/[/\\]/).pop() || ruta;

  useEffect(() => {
    if (!ruta) return;

    let activo = true;

    const cargarImagen = async () => {
      try {
        const response = await fetch(
          // ✅ /imagenEntregas — antes era /imagen (endpoint incorrecto)
          `${API_BASE}/imagenEntregas?ruta=${encodeURIComponent(ruta)}`,
          {
            headers:     { Authorization: `Bearer ${token}` },
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error();

        const blob = await response.blob();
        if (activo) setImgSrc(URL.createObjectURL(blob));
      } catch {
        if (activo) setImgError(true);
      }
    };

    cargarImagen();
    return () => { activo = false; };
  }, [ruta, token]);

  if (!ruta) return <span className="vi-no-imagen">—</span>;

  return (
    <>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={nombre}
          className="vi-miniatura"
          onClick={() => setOpen(true)}
        />
      ) : imgError ? (
        <span className="vi-no-imagen">⚠</span>
      ) : (
        <Spinner dark />
      )}

      {open && (
        <div className="vi-overlay" onClick={() => setOpen(false)}>
          <div className="vi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vi-modal-header">
              <span className="vi-modal-nombre">{nombre}</span>
              <button className="vi-modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>

            {imgSrc && (
              <img src={imgSrc} alt={nombre} className="vi-modal-img" />
            )}

            {!imgSrc && !imgError && (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spinner dark /> Cargando imagen...
              </div>
            )}

            {imgError && (
              <div style={{ textAlign: "center", padding: "2rem", color: "#b91c1c" }}>
                ⚠ No se pudo cargar la imagen
              </div>
            )}

            <p className="vi-modal-ruta">{ruta}</p>
          </div>
        </div>
      )}
    </>
  );
}

function ImagenBadge({ count }) {
  const badgeClass =
    count === 3 ? "vi-badge vi-badge--complete" :
    count === 0 ? "vi-badge vi-badge--empty"    :
                  "vi-badge vi-badge--partial";

  return <span className={badgeClass}>{count}/3</span>;
}

function Stat({ label, value, color }) {
  return (
    <div className="vi-stat-card" style={{ borderTopColor: color }}>
      <span className="vi-stat-val" style={{ color }}>{value}</span>
      <span className="vi-stat-lbl">{label}</span>
    </div>
  );
}

function Spinner({ dark }) {
  return (
    <span className={`vi-spinner ${dark ? "vi-spinner--dark" : "vi-spinner--light"}`} />
  );
}